import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import Alert from "../../Common/Alert";
import DatatableFunc from "../../Common/DatatableFunc";
import Http from "../../Common/Http";
import IsLogin from "../../Common/IsLogin";
import ReactDataTable from "../../Common/ReactDataTable";

const HandiRcvVsPosSales = () => {
  const location = useLocation();

  const [kitchenStaff, setKitchenStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [alert, setAlert] = useState(null);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [inputs, setInputs] = useState({
    from_date: getTodayDate(),
    to_date: getTodayDate(),
    kitchen_staff_id: "",
  });

  const showAlert = (message, type) => {
    setAlert({ message, type });

    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  const fetchKitchenStaff = async () => {
    try {
      const res = await Http.get("/staff2?user_type=kitchen");
      const list = res.data || [];

      const mapped = (list || []).map((item) => {
        const code = item.code || item.staff_code || item.s_code || item.value || "";
        const rawName = item.label || item.name || item.staff_name || "";

        const name =
          typeof rawName === "string" && rawName.includes(" - ")
            ? rawName.split(" - ").slice(1).join(" - ").trim()
            : rawName;

        const label = (name && String(name).trim()) || code || `Staff ${item.value}`;

        return {
          ...item,
          label,
        };
      });

      setKitchenStaff(mapped);
    } catch (error) {
      console.error("Error fetching kitchen staff:", error);
      showAlert("Error fetching kitchen staff", "error");
    }
  };

  const fetchData = () => {
    if (inputs.from_date > inputs.to_date) {
      showAlert("From date cannot be greater than To date", "error");
      return;
    }

    setLoading(true);

    const params = new URLSearchParams();
    params.append("user_type", "kitchen");

    if (inputs.kitchen_staff_id && inputs.kitchen_staff_id !== "") {
      params.append("kitchen_staff_id", inputs.kitchen_staff_id);
    }

    // ✅ Date range filters - always include
    if (inputs.from_date) {
      params.append("from_date", inputs.from_date);
    }

    if (inputs.to_date) {
      params.append("to_date", inputs.to_date);
    }

    Http.get(`/handi-receive-vs-pos-sale?${params.toString()}`)
      .then((res) => {
        setReportData(res.data?.data || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching Handi receive vs POS sale data:", error);
        setLoading(false);
        showAlert("Error fetching data", "error");
      });
  };

  useEffect(() => {
    DatatableFunc();
    fetchKitchenStaff();
    fetchData();
  }, []);

  useEffect(() => {
    if (!location.state) return;

    showAlert(location.state.message, location.state.type);
  }, [location]);

  const selectHandleChange = (selectedOption, actionMeta) => {
    const name = actionMeta.name;
    const value = selectedOption ? selectedOption.value : "";

    setInputs((values) => ({
      ...values,
      [name]: value,
    }));
  };

  const formSubmit = () => {
    fetchData();
  };

  const clearFilters = () => {
    setInputs({
      from_date: getTodayDate(),
      to_date: getTodayDate(),
      kitchen_staff_id: "",
    });

    setTimeout(() => {
      fetchData();
    }, 100);
  };

  const kitchenCols = useMemo(() => {
    const baseCols = [
      { field: "product_name", header: "Product Name" },
    ];

    if (!reportData.length) {
      return baseCols;
    }

    // collect union of keys from all rows so columns exist even when
    // a particular key (eg. stock_qty_0_sold) is missing from the first row
    const allKeysSet = (reportData || []).reduce((set, row) => {
      Object.keys(row || {}).forEach((k) => set.add(k));
      return set;
    }, new Set());

    const dynamicKeys = Array.from(allKeysSet).filter((k) => k.startsWith("stock_qty_")).sort();

    const dynamicCols = dynamicKeys.map((key) => {
      const match = key.match(/^stock_qty_(\d+)(?:_(.+))?$/);
      const staffId = match ? match[1] : key.replace("stock_qty_", "");
      const suffix = match && match[2] ? match[2].toLowerCase() : "";

      const staff = kitchenStaff.find((item) => String(item.value) === String(staffId));

      let headerLabel = staff?.label || `Staff ${staffId}`;

      if (suffix) {
        if (staffId === "0" && suffix === "sold") {
          headerLabel = "Others (Sold)";
        } else if (staffId === "0" && (suffix === "recieve" || suffix === "receive" || suffix === "received")) {
          headerLabel = "Others (Received)";
        } else if (suffix === "recieve" || suffix === "receive" || suffix === "received") {
          headerLabel = `${headerLabel} (Received)`;
        } else if (suffix === "sold") {
          headerLabel = `${headerLabel} (Sold)`;
        } else {
          headerLabel = `${headerLabel} (${suffix})`;
        }
      }

      return {
        field: key,
        header: headerLabel,
      };
    });
    return [...baseCols, ...dynamicCols];
  }, [reportData, kitchenStaff]);

  return (
    <div>
      <IsLogin />

      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">
            Handi Receive vs Position Sales Report
          </h5>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          {/* FILTER CARD */}
          <div className="card pd-sm-40">
            <div className="row">

              {/* FROM DATE */}
              <div className="col-lg-3">
                <label>From Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={inputs.from_date}
                  onChange={(e) =>
                    setInputs({ ...inputs, from_date: e.target.value })
                  }
                />
              </div>

              {/* TO DATE */}
              <div className="col-lg-3">
                <label>To Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={inputs.to_date}
                  onChange={(e) =>
                    setInputs({ ...inputs, to_date: e.target.value })
                  }
                />
              </div>

              {/* STAFF SELECT */}
              <div className="col-lg-4">
                <label>Select Handi</label>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  name="kitchen_staff_id"
                  options={kitchenStaff.filter(
                    (staff) =>
                      staff.value &&
                      staff.value !== "" &&
                      staff.label !== "Select Staff"
                  )}
                  onChange={selectHandleChange}
                  isClearable={true}
                />
              </div>

              {/* ACTION BUTTONS */}
              <div className="col-lg-2 d-flex align-items-end gap-2">
                <button
                  className="btn btn-primary w-100"
                  onClick={formSubmit}
                  disabled={loading}
                >
                  <i className="fa fa-search mg-r-10"></i>
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  className="btn btn-secondary w-100"
                  onClick={clearFilters}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="card pd-20 pd-sm-40 mt-4">
            <h5 className="card-body-title">
                Handi Receive vs Position Sales Report
            </h5>
            <hr />

            <ReactDataTable
              cols={kitchenCols}
              receiveData={reportData}
              isLoading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HandiRcvVsPosSales;